import { Component, HostBinding, Input, ChangeDetectionStrategy, ViewEncapsulation, ViewChild, OnInit, forwardRef, ContentChildren,
    QueryList, ChangeDetectorRef, AfterViewInit, EventEmitter, Output, OnDestroy, Directive, OnChanges,
    SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatChipList } from '@angular/material/chips';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { startWith, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CdkDragDrop, moveItemInArray, CdkDropList, transferArrayItem, CdkDrag } from '@angular/cdk/drag-drop';

import { BaseDirective } from '../../base.directive';
import { EuiChipComponent } from '../eui-chip/eui-chip.component';
import { EuiChipDragDrop } from './models/eui-chip-drag-drop.model';
import { EuiChip } from '../eui-chip/models/eui-chip.model';
import { EuiChipListDragService } from './services/eui-chip-list-drag.service';

@Component({
    templateUrl: './eui-chip-list.component.html',
    selector: 'eui-chip-list, div[euiChipList], ul[euiChipList]',
    styleUrls: ['./styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class EuiChipListComponent extends BaseDirective implements OnInit, AfterViewInit, OnDestroy, OnChanges {

    @Input() chips: any[];
    @Input() toggleLinkMoreLabel: string = null;
    @Input() toggleLinkLessLabel: string = null;
    @Input() chipsSortOrder: ('ASC' | 'DESC') = 'ASC';
    @Input() dragAndDropSourceName: string;
    @Input() dragAndDropConnectedTo: string[] = [];
    @Input() ariaLabel = 'eUI Chip List';

    @Input()
    get isChipsRemovable() { return this._isChipsRemovable; }
    set isChipsRemovable(value) { this._isChipsRemovable = coerceBooleanProperty(value); }
    _isChipsRemovable = false;

    @Input()
    get maxVisibleChipsCount() { return this._maxVisibleChipsCount; }
    set maxVisibleChipsCount(value) { this._maxVisibleChipsCount = coerceNumberProperty(value); }
    _maxVisibleChipsCount = null;

    @Input()
    get chipsLabelTruncateCount() { return this._chipsLabelTruncateCount; }
    set chipsLabelTruncateCount(value) { this._chipsLabelTruncateCount = coerceNumberProperty(value); }
    _chipsLabelTruncateCount = null;

    @Input()
    get isMaxVisibleChipsOpened() { return this._isMaxVisibleChipsOpened; }
    set isMaxVisibleChipsOpened(value) { this._isMaxVisibleChipsOpened = coerceBooleanProperty(value); }
    _isMaxVisibleChipsOpened = false;

    @Input()
    get isChipsSorted() { return this._isChipsSorted; }
    set isChipsSorted(value) { this._isChipsSorted = coerceBooleanProperty(value); }
    _isChipsSorted = false;

    @Input()
    get isChipsDragAndDrop() { return this._isChipsDragAndDrop; }
    set isChipsDragAndDrop(value) { this._isChipsDragAndDrop = coerceBooleanProperty(value); }
    _isChipsDragAndDrop = false;

    @Output() chipsInit = new EventEmitter<{ chipList: MatChipList, chipsListDragAndDropItems: CdkDropList }>();
    @Output() chipRemove = new EventEmitter<{ chips: EuiChip[], removed: EuiChip }>();
    @Output() chipDragStarted = new EventEmitter<EuiChipDragDrop>();
    @Output() chipDragReleased = new EventEmitter<EuiChipDragDrop>();
    @Output() chipDropped = new EventEmitter<EuiChipDragDrop>();

    @HostBinding('class')
    get cssClasses() {
        return [
            super.getCssClasses('eui-chip-list'),
        ].join(' ').trim();
    }

    public droppableArea: CdkDragDrop<any[]>[];
    public connectComponent: CdkDropList[] = [];
    public e2eAttr = 'eui-chip-list';

    @ContentChildren(forwardRef(() => EuiChipComponent)) euiChipsContent: QueryList<EuiChipComponent>;
    @ViewChild('chipList', { read: MatChipList, static: true }) chipList: MatChipList;
    @ViewChild('chipListDropList', { read: CdkDropList }) chipListDropList: CdkDropList;
    @ViewChild('chipListDragAndDropItems', { read: CdkDropList }) chipListDragAndDropItems: CdkDropList;

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private cd: ChangeDetectorRef, private euiChipListDragService: EuiChipListDragService, private domSanitizer: DomSanitizer) {
        super();
    }

    ngOnChanges(c: SimpleChanges) {
        if (c.chips && this.isChipsSorted) {
            this.chips = this.orderItems(this.chips, this.chipsSortOrder);
        }
    }

    ngOnInit() {
        this.chipsInit.emit({ chipList: this.chipList, chipsListDragAndDropItems: this.chipListDragAndDropItems });
        if (this.isChipsSorted && this.chips) {
            this.chips = this.orderItems(this.chips, this.chipsSortOrder);
        }
    }

    ngAfterViewInit() {
        if (this.dragAndDropSourceName) {
            this.euiChipListDragService.setComponentConnector(this.dragAndDropSourceName);
        }

        if (this.dragAndDropConnectedTo) {
            this.euiChipListDragService.setDroppableArea(this.chipListDropList, this.dragAndDropConnectedTo);
            this.dragAndDropConnectedTo.map((connectTo) => {
                this.connectComponent = this.euiChipListDragService.componentConnector[connectTo];
            });
        }

        setTimeout(() => {
            this.droppableArea = this.euiChipListDragService.droppableArea[this.dragAndDropSourceName];
        });

        Object.keys(this.euiChipListDragService.componentConnector).map((connectTo) => {
            this.euiChipListDragService.componentConnector[connectTo].push(this.chipListDragAndDropItems);
        });

        if (this.euiChipsContent.toArray()) {
            this.euiChipsContent.changes.pipe(startWith(true), takeUntil(this.destroy$)).subscribe((c) => {
                this.chips = this.euiChipsContent.toArray().map((chip) => {
                    return Object.assign(chip, { euiInternalId: this.generateId() });
                });

                if (this.isChipsSorted) {
                    this.chips = this.orderItems(this.chips, this.chipsSortOrder);
                }
                this.cd.detectChanges();
            });
        }
    }

    ngOnDestroy() {
        this.euiChipListDragService.resetConnector();
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public toggleTags() {
        this.isMaxVisibleChipsOpened = !this.isMaxVisibleChipsOpened;
    }

    public getChipContent(euiChip: any) {
        return euiChip.chipLabel ? this.domSanitizer.bypassSecurityTrustHtml(euiChip.chipLabel.nativeElement.innerHTML) : euiChip.label;
    }

    public getChipLabel(euiChip: any) {
        return euiChip.chipLabel ? euiChip.chipLabel.nativeElement.textContent : euiChip.label;
    }

    public getChipIconBefore(euiChip: any) {
        return euiChip.iconBefore;
    }

    public getChipIconAfter(euiChip: any) {
        return euiChip.iconAfter;
    }

    public onRemove(removed: any) {
        const chips = this.euiChipsContent.toArray().length > 0 ? this.chips.map(c => c.data) : this.chips;
        const index = chips.indexOf(removed);

        this.chips.splice(index, 1);

        this.chipRemove.emit({ chips: this.euiChipsContent.toArray().length > 0 ? this.chips.map(c => c.data) : this.chips, removed });
    }

    public trackByFn(index: number, euiChip: any) {
        return euiChip.euiInternalId ? euiChip.euiInternalId : euiChip.id;
    }

    public drop(e: CdkDragDrop<string[]>) {
        if (e.previousContainer === e.container) {
            moveItemInArray(e.container.data['chips'], e.previousIndex, e.currentIndex);
        } else {
            transferArrayItem(e.previousContainer.data['chips'], e.container.data['chips'], e.previousIndex, e.currentIndex);

            this.chips = e.container.data['chips'].map((item: any) => {
                return item;
            });
        }

        this.chipDropped.emit(new EuiChipDragDrop({
            type: 'dropped',
            sourceName: this.dragAndDropSourceName,
            chip: e.item.data,
            chips: e.container.data['chips'],
        }));
    }

    public onDragStarted(event: CdkDrag) {
        this.chipDragStarted.emit(new EuiChipDragDrop({
            type: 'started',
            sourceName: this.dragAndDropSourceName,
            chip: event['source'].data,
            chips: this.chips,
        }));
    }

    public onDragReleased(event: CdkDrag) {
        this.chipDragReleased.emit(new EuiChipDragDrop({
            type: 'released',
            sourceName: this.dragAndDropSourceName,
            chip: event['source'].data,
            chips: this.chips,
        }));
    }

    private generateId() {
        return '_' + Math.random().toString(36).substr(2, 9);
    }

    private orderItems(tab: any, sortOrder: string) {
        tab.sort((a: any, b: any) => {

            const aObj = a.chipLabel ? a.chipLabel.nativeElement.textContent.toLowerCase() : a.label.toLowerCase();
            const bObj = b.chipLabel ? b.chipLabel.nativeElement.textContent.toLowerCase() : b.label.toLowerCase();

            if (sortOrder === 'ASC') {
                if (aObj < bObj) {
                    return -1;
                }
                if (aObj > bObj) {
                    return 1;
                }
                return 0;
            } else {
                if (aObj > bObj) {
                    return -1;
                }
                if (aObj < bObj) {
                    return 1;
                }
                return 0;
            }
        });

        return tab;
    }
}

/* tslint:disable */
@Directive({ selector: 'euiChipListAppendContent' })
export class EuiChipListAppendContentDirective { }
/* tslint:enable */
